// Construtor puro das linhas do painel Info. Sem React, sem store — testável
// isoladamente. Recebe a barra sob o cursor + extras de variação + o gráfico
// hovered (para enumerar indicadores) e formatadores injetados.
//
// Contrato de linha: { id, title, value, isHeader?, tone? }
//   - isHeader: linha de cabeçalho de seção (sem valor, com separador).
//   - tone: "up" | "down" — só para colorir variação.

export const NA = "---";

// Extrai um número de um campo que pode vir como número OU como objeto
// processado ({ volume: n }) — o indicador de volume transforma volume/
// financialVolume/tickCount. Espelha OHLCTooltip.jsx:77-108.
const getNumeric = (x) => {
  if (typeof x === "number") return x;
  if (x && typeof x.volume === "number") return x.volume;
  return null;
};

const isFiniteNum = (x) => typeof x === "number" && Number.isFinite(x);

const capitalize = (k) =>
  typeof k === "string" && k.length
    ? k.charAt(0).toUpperCase() + k.slice(1)
    : String(k);

// Resolve um indicador na registry (mesma varredura de ChartTooltips.findRegistryEntry).
const findRegistryEntry = (registry, key) => {
  if (!registry || !key) return null;
  for (const categoryKey of Object.keys(registry)) {
    const entry = registry[categoryKey]?.indicators?.[key];
    if (entry) return entry;
  }
  return null;
};

/**
 * Linhas de um único indicador na barra sob o cursor.
 * Valor mora em bar[renderKey] (ou bar[key]): número (single-plot) ou objeto
 * (multi-plot, ex.: Bollinger { top, middle, bottom }).
 */
const buildIndicatorRows = (indicator, bar, registry, fmt) => {
  const entry = findRegistryEntry(registry, indicator.key);
  const displayName =
    indicator.displayName || entry?.displayName || indicator.key || "Indicador";
  const idBase = indicator.id || indicator.renderKey || indicator.key;

  const raw =
    (indicator.renderKey != null ? bar?.[indicator.renderKey] : undefined) ??
    (indicator.key != null ? bar?.[indicator.key] : undefined);

  // Número direto (ou {volume:n})
  const asNum = getNumeric(raw);
  if (asNum !== null || raw == null) {
    return [
      {
        id: idBase,
        title: displayName,
        value: isFiniteNum(asNum) ? fmt.num(asNum) : NA,
      },
    ];
  }

  // Objeto multi-plot: enumera as chaves numéricas
  if (typeof raw === "object") {
    const numericEntries = Object.entries(raw).filter(
      ([, v]) => typeof v === "number",
    );

    if (numericEntries.length <= 1) {
      const v = numericEntries[0]?.[1];
      return [
        {
          id: idBase,
          title: displayName,
          value: isFiniteNum(v) ? fmt.num(v) : NA,
        },
      ];
    }

    const rows = [{ id: idBase, title: displayName, isHeader: true }];
    numericEntries.forEach(([k, v]) => {
      rows.push({
        id: `${idBase}:${k}`,
        title: capitalize(k),
        value: isFiniteNum(v) ? fmt.num(v) : NA,
      });
    });
    return rows;
  }

  return [{ id: idBase, title: displayName, value: NA }];
};

/**
 * Monta todas as linhas do painel Info.
 * @param {object} args
 * @param {object} args.bar barra sob o cursor
 * @param {number} [args.prevClose] close da barra anterior
 * @param {number} [args.dayPrevClose] close do dia anterior
 * @param {object} args.chart gráfico hovered (para chart.indicators)
 * @param {string} args.symbol símbolo do gráfico hovered
 * @param {object} [args.registry] indicatorRegistry (fallback de displayName)
 * @param {number|null} [args.ajuste] settlement D-1 do símbolo (#437) — dado
 *   CORRENTE (não é per-bar: não muda ao passear o cursor pelo histórico,
 *   paridade Silverlight). null ⇒ linha não aparece; NUNCA usar PC/fechamento
 *   como substituto (em futuro, fechamento ≠ ajuste).
 * @param {number|null} [args.vwap] VWAP da SESSÃO corrente (campo VW do quote,
 *   calculado pelo servidor) — dado CORRENTE como o ajuste, não per-bar.
 *   null ⇒ linha não aparece; nunca calcular localmente como substituto.
 * @param {boolean} [args.isDaily] periodicidade diária+ (1D/1W/1M): o par
 *   "Var. dia" é omitido (duplicaria "Variação").
 * @param {{price:Function, num:Function, pct:Function, date:Function}} args.fmt
 * @returns {Array<{id,title,value,isHeader?,tone?}>}
 */
export const buildInfoRows = ({
  bar,
  prevClose,
  dayPrevClose,
  chart,
  symbol,
  registry,
  ajuste,
  ajusteD0,
  fase,
  teorico,
  emAberto,
  vwap,
  isDaily,
  fmt,
}) => {
  if (!bar) return [];

  const rows = [];
  const priceRow = (id, title, v) => ({
    id,
    title,
    value: isFiniteNum(v) ? fmt.price(v) : NA,
  });

  // --- Bloco da barra ---
  rows.push({ id: "symbol", title: "Símbolo", value: symbol || NA });
  rows.push({
    id: "date",
    title: "Data",
    value: bar.date != null ? fmt.date(bar.date) : NA,
  });
  rows.push(priceRow("open", "Abertura", bar.open));
  rows.push(priceRow("high", "Máxima", bar.high));
  rows.push(priceRow("low", "Mínima", bar.low));
  rows.push(priceRow("close", "Fechamento", bar.close));

  // Variação vs. barra anterior
  const varRows = (idBase, label, ref) => {
    if (!isFiniteNum(ref) || !isFiniteNum(bar.close)) {
      rows.push({ id: idBase, title: label, value: NA });
      rows.push({ id: `${idBase}Pct`, title: `${label} %`, value: NA });
      return;
    }
    const diff = bar.close - ref;
    const pct = ref !== 0 ? (diff / ref) * 100 : NaN;
    const tone = diff > 0 ? "up" : diff < 0 ? "down" : undefined;
    const sign = diff > 0 ? "+" : "";
    rows.push({
      id: idBase,
      title: label,
      value: `${sign}${fmt.price(diff)}`,
      tone,
    });
    rows.push({
      id: `${idBase}Pct`,
      title: `${label} %`,
      value: isFiniteNum(pct) ? `${sign}${fmt.pct(pct)}` : NA,
      tone,
    });
  };
  varRows("var", "Variação", prevClose);
  // Em periodicidade diária+ a barra anterior JÁ É o dia anterior — o par
  // "Var. dia" duplicaria "Variação" com outro rótulo. Só faz sentido em
  // intraday.
  if (!isDaily) {
    varRows("varDay", "Var. dia", dayPrevClose);
  }

  // Volume / financeiro / negócios (inteiros → fmt.int)
  const numRow = (id, title, v) => {
    const n = getNumeric(v);
    rows.push({ id, title, value: isFiniteNum(n) ? fmt.int(n) : NA });
  };
  numRow("volume", "Volume", bar.volume);
  numRow("financialVolume", "Vol. financeiro", bar.financialVolume);
  numRow("tickCount", "Negócios", bar.tickCount);

  // VWAP da sessão (campo VW do quote, servidor) — dado corrente, some quando
  // não há quote com VW. Adição do WebCharts26 (o Info do Silverlight não tinha).
  if (isFiniteNum(vwap) && vwap > 0) {
    rows.push(priceRow("vwap", "VWAP", vwap));
  }

  // Ajuste D-1 (settlement do pregão anterior, #437) — última linha do bloco.
  // Label "Ajuste D-1" (não "Ajuste", do Silverlight): desambigua do settlement
  // apurado hoje (SP), que também viaja no payload RP.
  // Só existe quando o servidor mandou RP com ps>0 (futuros); à vista não tem.
  if (isFiniteNum(ajuste) && ajuste > 0) {
    rows.push(priceRow("ajuste", "Ajuste D-1", ajuste));
  }

  // Ajuste D0 (settlement apurado HOJE, campo SP do RP — fim de tarde). Some
  // enquanto a B3 não publica; nunca fallback (mesma regra do D-1).
  if (isFiniteNum(ajusteD0) && ajusteD0 > 0) {
    rows.push(priceRow("ajusteD0", "Ajuste D0", ajusteD0));
  }

  // Fase de negociação (evento "SS", marketdata#25) — texto do Feeder, exibido
  // como veio ("NEGOCIACAO", "LEILAO", "PRE ABERTURA"…). Sempre visível quando
  // houver dado (decisão 04/08); some sem dado, nunca placeholder.
  if (typeof fase === "string" && fase.length > 0) {
    rows.push({ id: "fase", title: "Fase", value: fase });
  }

  // Teórico de abertura (evento "TP") — o CALLER só passa valor quando a fase
  // está em leilão (isAuctionState); fora de leilão o teórico é stale.
  if (isFiniteNum(teorico) && teorico > 0) {
    rows.push(priceRow("teorico", "Teórico", teorico));
  }

  // Contratos em aberto (evento "OI") — inteiro, não preço.
  if (isFiniteNum(emAberto) && emAberto > 0) {
    rows.push({ id: "emAberto", title: "Em Aberto", value: fmt.int(emAberto) });
  }

  // --- Bloco de indicadores (todos os chart areas do gráfico) ---
  const indicators = chart?.indicators || [];
  if (indicators.length > 0) {
    rows.push({
      id: "indicators-header",
      title: "Indicadores",
      isHeader: true,
    });
    indicators.forEach((ind) => {
      buildIndicatorRows(ind, bar, registry, fmt).forEach((r) => rows.push(r));
    });
  }

  return rows;
};
